// =============================================================================
// Next-test forward-look — recommends the single most-informative core
// marker to add when the engine returned `insufficient_data`.
//
// Approach: for each core marker the user did NOT provide
// (from {ferritin, hemoglobin, MCV, TSAT}), sweep a representative grid
// of plausible values, re-classify against the user's existing inputs,
// and count how many sweep values resolve to a substantive (non-meta)
// pattern. The marker with the highest resolution rate is the one most
// likely to yield a clear pattern read on the next blood draw.
//
// This is not a probabilistic forecast of the user's next result. It's
// a coverage statement: "of the patterns this marker could place you
// into given everything else you've told us, X% are substantive reads."
// A high rate means "this marker generally resolves the picture for
// inputs like yours;" a low rate means "even with this marker, the
// engine still can't classify — try a different marker (or a clinician)."
//
// Tie-breaking when two markers have the same resolution rate:
//   ferritin > hemoglobin > MCV > TSAT
// — ferritin first because the literature consistently identifies it
// as the single most informative iron marker. This priority order is
// also reflected in the engine's pattern arbitration logic (Mei 2021,
// Mei 2024, Galetti 2021).
//
// Pure function; no engine state. classify is passed as a callback to
// avoid circular imports between this module and classify.ts.
// =============================================================================

import type { Classification, LabInputs } from './types.js';

/** Display strings used in user-facing copy. */
const MARKER_DISPLAY: Record<CoreMarker, string> = {
  ferritin: 'ferritin',
  hemoglobin: 'hemoglobin',
  mcv: 'MCV',
  tsat: 'TSAT',
};

/**
 * Sweep grids per core marker — representative values across the
 * realistic range. Numbers chosen to land in distinct engine zones so
 * the sweep meaningfully exercises the trigger thresholds. Units are
 * the engine's internal SI:
 *   ferritin:   μg/L
 *   hemoglobin: g/L
 *   MCV:        fL
 *   TSAT:       %
 */
const SWEEP_GRIDS: Record<CoreMarker, number[]> = {
  ferritin: [5, 12, 20, 35, 50, 80, 150],
  hemoglobin: [80, 100, 110, 120, 135, 145],
  mcv: [70, 80, 88, 95, 105],
  tsat: [5, 12, 25, 40, 55],
};

/** Pattern ids the engine returns when it CAN'T give a substantive read. */
const META_PATTERNS = new Set<string>([
  'insufficient_data',
  'no_established_pattern',
  'mixed_picture_multiple_patterns',
]);

/** The four markers Rule 6 (methodology) names as the core panel. */
export type CoreMarker = 'ferritin' | 'hemoglobin' | 'mcv' | 'tsat';

/** Tie-break order: ferritin first (literature-anchored primacy). */
const TIEBREAK_ORDER: CoreMarker[] = ['ferritin', 'hemoglobin', 'mcv', 'tsat'];

export interface NextTestRecommendation {
  /** Internal marker key matching LabInputs property names. */
  marker: CoreMarker;
  /** Display name for user-facing copy ("ferritin", "MCV", ...). */
  displayName: string;
  /**
   * Fraction of sweep values that resolved to a substantive pattern
   * (0–1). Higher means adding this marker is more likely to give a
   * clear pattern read for inputs like the user's.
   */
  resolutionRate: number;
  /** True when resolutionRate >= 0.5. */
  resolvesLikely: boolean;
}

/**
 * Recommend the single most-informative core marker to add given the
 * user's current inputs. Returns undefined when:
 *   - All four core markers are already present (no marker to add)
 *   - Every sweep across every missing marker yields a meta pattern
 *     (genuinely unresolvable from current inputs)
 *
 * `classifyFn` is the engine's full classify function; passed as a
 * callback to keep this module free of circular imports.
 */
export function recommendNextTest(
  inputs: LabInputs,
  classifyFn: (inputs: LabInputs) => Classification,
): NextTestRecommendation | undefined {
  const missing = TIEBREAK_ORDER.filter(
    (m) => inputs[m as keyof LabInputs] === undefined,
  );
  if (missing.length === 0) return undefined;

  const results: NextTestRecommendation[] = [];

  for (const marker of missing) {
    const sweeps = SWEEP_GRIDS[marker];
    let resolvedCount = 0;
    for (const value of sweeps) {
      const trial = { ...inputs, [marker]: value } as LabInputs;
      const result = classifyFn(trial);
      if (!META_PATTERNS.has(result.patternId)) resolvedCount++;
    }
    const resolutionRate = resolvedCount / sweeps.length;
    results.push({
      marker,
      displayName: MARKER_DISPLAY[marker],
      resolutionRate,
      resolvesLikely: resolutionRate >= 0.5,
    });
  }

  // Sort by resolution rate (desc); ties broken by TIEBREAK_ORDER index.
  results.sort((a, b) => {
    if (b.resolutionRate !== a.resolutionRate) {
      return b.resolutionRate - a.resolutionRate;
    }
    return TIEBREAK_ORDER.indexOf(a.marker) - TIEBREAK_ORDER.indexOf(b.marker);
  });

  // Always return the top recommendation (sorted by resolution rate then
  // tiebreak order). When `resolutionRate === 0` across every missing
  // marker, the user's input set is genuinely undiagnosable from any
  // single core-marker addition — but recommending the highest-priority
  // missing marker is still useful: it moves the result from
  // `insufficient_data` to `no_established_pattern`, which carries a
  // clinical-judgment routing that's more actionable than a stuck state.
  // Callers can read `resolvesLikely` to distinguish strong vs weak
  // recommendations and adjust the user-facing copy accordingly.
  return results[0]!;
}
